import { client } from "helper/apolloConfig";
import { globalVariables } from "helper/globalVariables";
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { updateHostProfileMutation } from "redux/features/hostSlice/profile/hostProfileGql";
import { setHostProfile } from "redux/features/hostSlice/profile/hostProfileSlice";
import { PhoneInput } from "shared/Components/phoneInput";
import { PopUpMessages } from "shared/Components/popUpMessages";
import { TextInput } from "shared/Components/textInput";
import { TextArea } from "shared/Components/textarea";
import { hostProfileResolver } from "validators/UserValidation";

const mobileRegex =
	/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;

export const HostProfile = forwardRef((props, ref) => {
	const { onSetLoading, isEditable, onProfileSuccess, onProfileError } =
		props || {};
	const formRef = useRef(null);
	const dispatch = useDispatch();
	const { auth } = useSelector((state) => state);
	const modalRef = useRef();
	const [phone, setPhone] = useState("");

	useImperativeHandle(ref, () => ({
		saveChanges() {
			onCheckValidation();
		},
	}));

	useEffect(() => {
		getHostProfile();
	}, []);

	const getHostProfile = async () => {
		if (auth?.hostProfileReducer?.hostProfile) {
			const hostProfile = auth?.hostProfileReducer?.hostProfile;
			setValue("firstName", hostProfile?.first_name);
			setValue("lastName", hostProfile?.last_name);
			setValue("email", hostProfile?.email);
			setValue("address", hostProfile?.location_address);
			setValue("phoneNumber", hostProfile?.phone_number);
			setValue("website", hostProfile?.website);
		}
	};

	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		watch,
	} = useForm({
		resolver: hostProfileResolver,
		defaultValues: {
			firstName: "",
			lastName: "",
			address: "",
			website: "",
			email: "",
			phoneNumber: "",
		},
	});

	const onHandleSubmit = async (credentials) => {
		if (
			credentials?.firstName &&
			credentials?.lastName &&
			credentials?.address &&
			credentials?.phoneNumber
		) {
			onSetLoading(true);
			const payload = {
				website: credentials?.website,
			};

			if (credentials?.firstName) {
				payload.first_name = credentials?.firstName;
			}
			if (credentials?.lastName) {
				payload.last_name = credentials?.lastName;
			}
			if (credentials?.address) {
				payload.location_address = credentials?.address;
			}
			if (credentials?.phoneNumber) {
				payload.phone_number = credentials?.phoneNumber;
			}

			try {
				const result = await client.mutate({
					mutation: updateHostProfileMutation,
					variables: { data: payload },
				});

				if (result?.data?.updateMyMemberProfile) {
					onProfileSuccess?.();
					dispatch(setHostProfile(result?.data?.updateMyMemberProfile));
					getHostProfile();
				}
				onSetLoading(false);
			} catch (error) {
				onProfileError?.(error);
				onSetLoading(false);
			}
		} else {
			modalRef.current?.openModal();
		}
	};

	const onCheckValidation = () => {
		handleSubmit(onHandleSubmit)();
	};

	return (
		<>
			<div className="mt-6" />
			<form
				className="flex justify-center lg:justify-start"
				onSubmit={handleSubmit(onHandleSubmit)}
				ref={formRef}
			>
				<div className="max-[400px]:w-[90%]">
					<TextInput
						register={register}
						name="firstName"
						placeholder={globalVariables.firstName}
						errorMsg={errors.firstName?.message}
						disabled={!isEditable}
						label={globalVariables.firstName}
					/>
					<div className="mt-5" />
					<TextInput
						register={register}
						name="lastName"
						placeholder={globalVariables.lastName}
						errorMsg={errors.lastName?.message}
						disabled={!isEditable}
						label={globalVariables.lastName}
					/>
					<div className="mt-5" />
					<TextArea
						register={register}
						name="address"
						placeholder={globalVariables.address}
						errorMsg={errors.address?.message}
						disabled={!isEditable}
						label={globalVariables.address}
					/>
					<div className="mt-5" />
					<TextInput
						register={register}
						name="email"
						placeholder={globalVariables.emailId}
						errorMsg={errors.email?.message}
						label={globalVariables.emailId}
						disabled
						textTransform="normal-case"
					/>
					<div className="mt-5" />
					<PhoneInput
						name="phoneNumber"
						placeholder={globalVariables.enterPhoneNumber}
						errorMsg={
							!mobileRegex.test(String(phone).toLowerCase())
								? errors.phoneNumber?.message
								: ""
						}
						label={globalVariables.phoneNumber}
						disabled={!isEditable}
						value={watch("phoneNumber")}
						onChange={(e) => {
							setValue("phoneNumber", e.target.value);
							setPhone(e.target.value);
						}}
					/>
					<div className="mt-5" />
					<TextInput
						register={register}
						name="website"
						placeholder={globalVariables.website}
						errorMsg={errors.website?.message}
						disabled={!isEditable}
						label={globalVariables.website}
						textTransform="normal-case"
					/>
				</div>
			</form>
			<PopUpMessages
				title={globalVariables.error}
				desc={globalVariables.addDataToSave}
				type={"pending"}
				ref={modalRef}
			/>
		</>
	);
});
